import { useQueries } from "@tanstack/react-query";
import { ServerTile } from "@entities/server";
import { useCatalogue } from "@entities/workspace";
import { api, keys, type ServerSummary, type Uuid } from "@shared/api";
import type { CustomEmoji } from "@shared/lib/emoji";
import { EmojiPicker, type PickerServer } from "@shared/ui/EmojiPicker";

/**
 * Панель знаков с колонкой серверов, как в Discord: этот сервер и остальные,
 * в которых человек состоит. Знаки остальных грузятся при открытии панели
 * (её содержимое монтируется только открытой) и держатся пять минут.
 *
 * Обёртка живёт здесь, а не в `shared/ui`: серверы и их значки — это
 * сущности, а `EmojiPicker` о них знать не должен.
 */
export function ServerEmojiPicker({
  serverId,
  custom,
  onPick,
  embedded,
}: {
  /** Сервер канала; `null` — личная переписка, тогда все серверы — чужие. */
  serverId: Uuid | null;
  custom: CustomEmoji[];
  onPick: (value: string) => void;
  embedded?: boolean;
}) {
  const servers = useCatalogue((state) => state.servers);
  const others = servers.filter((server) => server.id !== serverId);
  const lists = useQueries({
    queries: others.map((server) => ({
      queryKey: keys.serverEmoji(server.id),
      queryFn: () => api.emoji.list(server.id).then((value) => value.items),
      staleTime: 5 * 60_000,
    })),
  });

  const picker = (server: ServerSummary): PickerServer => ({
    id: server.id,
    name: server.name,
    tile: (size) => <ServerTile id={server.id} name={server.name} iconId={server.icon_id} size={size} />,
  });

  const current = serverId ? servers.find((server) => server.id === serverId) : undefined;

  return (
    <EmojiPicker
      custom={custom}
      onPick={onPick}
      embedded={embedded}
      here={current ? picker(current) : null}
      others={others
        .map((server, index) => ({
          ...picker(server),
          emoji: (lists[index]?.data ?? []).map((item) => ({
            id: item.id,
            name: item.name,
            file_id: item.file_id,
            server_id: item.server_id,
            server_name: server.name,
          })),
        }))
        .filter((server) => server.emoji.length > 0)}
    />
  );
}
